import { useEffect, useState } from 'react';
import { SortDirectionEnum, useFindByQuery } from '../../graphql';
import { Group } from '../domain';
import { useCurrentAccount, useCurrentWorkspace } from '../../auth';
import { RoleKey, RoleNameMap } from '../../account/hooks/useRoles';

export const useGroups = (workspaceId?: string) => {
  const { account } = useCurrentAccount();
  const currentWorkspace = useCurrentWorkspace();
  // Workspace resolution priority:
  // 1) Explicit workspaceId passed to the hook
  // 2) Current workspace from storage/impersonation
  // 3) Account.workspace (fallback when no other workspace is available)
  const effectiveWorkspaceId =
    workspaceId || currentWorkspace?.id || account?.workspace?.id;
  const [groups, setGroups] = useState<Group[]>([]);
  const [filter, setFilter] = useState<any | undefined>();

  useEffect(() => {
    if (!account || !effectiveWorkspaceId) return;

    const filter: any = {
      workspace: { eq: effectiveWorkspaceId },
    };

    // Only restrict groups for *purely* Restricted accounts:
    // - If the user has ANY non-Restricted role (General, Workspace Admin, Global Admin),
    //   they should see all groups in the workspace.
    const roles = account.roles || [];
    const hasNonRestrictedRole = roles.some(
      (role: any) => role.name !== RoleNameMap[RoleKey.RESTRICTED]
    );
    const isStrictlyRestricted = roles.length > 0 && !hasNonRestrictedRole;
    if (isStrictlyRestricted) {
      // Strictly Restricted users should only see groups they were added to
      // If they have no groups, filter by empty array to show nothing
      const assignedGroupIds = account.groups?.map((group: any) => group.id) || [];
      filter.id = { in: assignedGroupIds };
    }

    setFilter(filter);
  }, [account, effectiveWorkspaceId]);

  const { data, isLoading, invalidateQuery } = useFindByQuery({
    modelName: 'group',
    query: GET_GROUPS,
    variables: {
      filter,
      orderBy: {
        field: 'name',
        order: SortDirectionEnum.Asc,
      },
    },
    options: {
      enabled: !!filter,
    },
  });

  useEffect(() => {
    if (!data) {
      return;
    }
    setGroups(data.items);
  }, [data]);

  return {
    groups,
    isLoading,
    invalidateQuery,
  };
};

const GET_GROUPS = `
  query findGroups($filter: GroupFilter, $orderBy: OrderByInput) {
    findGroups(filter: $filter, orderBy: $orderBy) {
      items {
        id
        name
      }
    }
  }
`;
